import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { CopilotContext } from '@Sage/ssm-web5';

const EntityCrudContent = ({ children, ...props }) => {
    const { copilotBarProps } = useContext(CopilotContext);
    const {
        height: copilotBarHeight,
        isMinimized: isCopilotBarMinimized,
        isOpen: isCopilotBarOpen,
        padding: copilotBarPadding,
    } = copilotBarProps;

    const containerRef = useRef(null);
    const previousPaddingBottomRef = useRef(null);

    const paddingBottom = useMemo(() => {
        return isCopilotBarOpen ? copilotBarPadding * 2 + copilotBarHeight : 0;
    }, [copilotBarHeight, copilotBarPadding, isCopilotBarOpen]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const previousPaddingBottom = previousPaddingBottomRef.current;
        const currentPaddingBottom = paddingBottom;

        // Check if paddingBottom actually changed
        if (previousPaddingBottom !== null && previousPaddingBottom !== currentPaddingBottom) {
            const { scrollHeight, scrollTop, clientHeight } = container;
            // Only maintain scroll position if we were already at the bottom
            const wasAtBottom = scrollHeight - scrollTop - clientHeight < 10; // 10px threshold

            if (wasAtBottom) {
                // Use requestAnimationFrame to ensure DOM has updated before scrolling
                requestAnimationFrame(() => {
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                });
            }
        }

        previousPaddingBottomRef.current = currentPaddingBottom;
    }, [paddingBottom]);

    const finalProps = useMemo(() => {
        const { ref, ...restProps } = props;
        return {
            ...restProps,
            className: classNames('entity-crud-content', props.className),
            style:
                paddingBottom > 0 && !isCopilotBarMinimized
                    ? {
                          paddingBottom,
                      }
                    : null,
        };
    }, [isCopilotBarMinimized, paddingBottom, props]);

    return (
        <div {...finalProps} ref={containerRef}>
            {children}
        </div>
    );
};

export default memo(EntityCrudContent);
